// EXPERIMENTAL offline fallback (2026-10-02). Only used when the phone has
// no signal. "Cache assets for this area" stores the server's compact pack
// (/api/field/offline-pack: ids, type, label, point or path - exactly what
// the user may already see through /nearby) in IndexedDB. Offline, a tiny
// matcher ranks cached assets by distance and alignment with the compass
// heading. As soon as the phone is back online the server's ranking (with
// road recognition) REPLACES this result - the UI says so.
import { angleDiff } from "./heading";
import { AREAS, tx } from "./outbox";

export interface PackAsset {
  asset_id: string;
  asset_type: string;
  kind: "point" | "line";
  label: string;
  lon?: number;
  lat?: number;
  path?: [number, number][];
}

export interface AreaPack {
  key: string;
  bbox?: number[];
  center: [number, number];
  radius_m: number;
  saved_at: number;
  assets: PackAsset[];
}

const R = 6371008.8;

/** Local equirectangular metres around (lat0, lon0) - fine at < 1 km. */
function toXY(lat0: number, lon0: number, lat: number, lon: number): [number, number] {
  const x = ((lon - lon0) * Math.PI) / 180 * R * Math.cos((lat0 * Math.PI) / 180);
  const y = ((lat - lat0) * Math.PI) / 180 * R;
  return [x, y];
}

function nearestOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): [number, number] {
  const dx = bx - ax, dy = by - ay;
  const L = dx * dx + dy * dy;
  const t = L === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L));
  return [ax + t * dx, ay + t * dy];
}

export async function saveArea(pack: { center: [number, number]; radius_m: number; assets: PackAsset[]; key?: string; bbox?: number[] }): Promise<void> {
  // a basemap pack's assets are stored under "pack:<id>" (packs.ts), so
  // deleting the pack deletes them too
  const key = pack.key ?? `${pack.center[1].toFixed(4)},${pack.center[0].toFixed(4)}`;
  await tx(AREAS, "readwrite", (s) => s.put({ ...pack, key, saved_at: Date.now() }));
}

export async function deleteArea(key: string): Promise<void> {
  await tx(AREAS, "readwrite", (s) => s.delete(key));
}

export async function listAreas(): Promise<AreaPack[]> {
  return (await tx<AreaPack[]>(AREAS, "readonly", (s) => s.getAll())) ?? [];
}

export async function clearAreas(): Promise<void> {
  await tx(AREAS, "readwrite", (s) => s.clear());
}

export interface OfflineCandidate extends PackAsset {
  distance_m: number;
  bearing_deg: number | null;
  relative_bearing_deg: number | null;
  score: number;
}

/** Rank cached assets around a fix (no road recognition offline). */
export async function matchOffline(lat: number, lon: number, heading: number | null, accuracy: number | null, n = 5): Promise<OfflineCandidate[]> {
  const sigma = Math.max(accuracy ?? 10, 6) * 1.5;
  const out: OfflineCandidate[] = [];
  const seen = new Set<string>();
  for (const area of await listAreas()) {
    for (const a of area.assets) {
      if (seen.has(a.asset_id)) continue;
      seen.add(a.asset_id);
      let qx: number, qy: number;
      if (a.kind === "point" && a.lat !== undefined && a.lon !== undefined) {
        [qx, qy] = toXY(lat, lon, a.lat, a.lon);
      } else if (a.path && a.path.length) {
        let best: [number, number] | null = null;
        let bd = Infinity;
        const pts = a.path.map(([plon, plat]) => toXY(lat, lon, plat, plon));
        for (let i = 0; i < pts.length; i++) {
          const q = i + 1 < pts.length ? nearestOnSegment(0, 0, pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]) : pts[i];
          const d = Math.hypot(q[0], q[1]);
          if (d < bd) {
            bd = d;
            best = q;
          }
        }
        if (!best) continue;
        [qx, qy] = best;
      } else continue;
      const d = Math.hypot(qx, qy);
      if (d > 300) continue;
      const b = d > 0.01 ? ((Math.atan2(qx, qy) * 180) / Math.PI + 360) % 360 : null;
      const rel = heading !== null && b !== null ? angleDiff(b, heading) : null;
      const sDist = Math.exp(-0.5 * (d / sigma) ** 2);
      const sHead = rel === null || d <= 2 ? 0.5 : Math.max(0, Math.cos((rel * Math.PI) / 180)) ** 2;
      const w = heading === null ? 0 : 0.3;
      out.push({ ...a, distance_m: Math.round(d * 10) / 10, bearing_deg: b, relative_bearing_deg: rel, score: (1 - w) * sDist + w * sHead * Math.sqrt(sDist) });
    }
  }
  out.sort((x, y) => y.score - x.score);
  return out.slice(0, Math.max(n, 5));
}
